import { getTopWhtContributingVendors } from '@/api/dashboardApi'
import HorizontalGraph from '@/components/dashboard/HorizontalGraph'
import { topContributingVendorsUIMetaData } from '@/constants/dashboardConstant'
import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

// ==================== Helpers ====================
const mapTopWhtContributingVendorsToItems = (data = []) => {
  if (!Array.isArray(data)) {
    return []
  }
  return data?.map(vendor => ({
    label: vendor?.vendorName,
    expenseValue: vendor?.expenseLiableToWhtValue,
    whtValue: vendor?.whtAmountValue,
    expenseDisplay: vendor?.expenseLiableToWht,
    whtDisplay: vendor?.whtAmount,
  }))
}

// Builds the graph footnote from the API's valueConversion (e.g. "millions")
// and currencyCode (e.g. "USD"). Returns the provided fallback when either
// value is missing so the UI never shows a partial note.
const buildValuesNote = (valueConversion, currencyCode, fallback) => {
  if (!valueConversion || !currencyCode) {
    return fallback
  }
  const unit =
    valueConversion.charAt(0).toUpperCase() + valueConversion.slice(1)
  return `*All values are in ${unit} & ${currencyCode}`
}

const TopWhtContributingVendorsGraph = () => {
  // ==================== Hooks ====================
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const appliedFilters = useSelector(
    state => state.dashboardFilter.appliedFilters
  )

  // ==================== State ====================
  const [items, setItems] = useState([])
  const [note, setNote] = useState(topContributingVendorsUIMetaData.note)
  const [dataLoaded, setDataLoaded] = useState(false)

  // ==================== Effects ====================
  useEffect(() => {
    if (!optionsLoaded) return
    const fetchData = async () => {
      setDataLoaded(false)
      try {
        const vendorsResponse =
          await getTopWhtContributingVendors(appliedFilters)
        processData(vendorsResponse)
      } finally {
        setDataLoaded(true)
      }
    }
    fetchData()
  }, [optionsLoaded, appliedFilters])

  // ==================== Component Helpers ====================
  const processData = vendorsResponse => {
    const vendorsData = vendorsResponse?.data?.data?.vendors
    if (vendorsResponse?.success && vendorsData) {
      setItems(mapTopWhtContributingVendorsToItems(vendorsData))
      setNote(
        buildValuesNote(
          vendorsResponse?.data?.data?.valueConversion,
          vendorsResponse?.data?.data?.currencyCode,
          topContributingVendorsUIMetaData.note
        )
      )
    } else {
      setItems([])
      setNote(topContributingVendorsUIMetaData.note)
    }
  }

  // ==================== Return ====================
  if (!dataLoaded) {
    return (
      <Box
        sx={{
          border: '1px solid var(--border-subtel)',
          backgroundColor: 'var(--white-color)',
          p: 2,
          flex: 1,
          minWidth: 300,
          minHeight: 500,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            color: 'var(--forbrand-primary)',
            fontSize: 'var(--font-size-lg)',
          }}
        >
          Loading...
        </Typography>
      </Box>
    )
  }

  return (
    <HorizontalGraph
      heading={topContributingVendorsUIMetaData.heading}
      legend={topContributingVendorsUIMetaData.legend}
      note={note}
      items={items}
    />
  )
}

export default TopWhtContributingVendorsGraph
